import { useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, XCircle, Calendar, Pause, Play, RefreshCw, Repeat } from "lucide-react";
import { toast } from "sonner";

const FREQS = [
  { key: "weekly",     label: "Weekly (10% off)" },
  { key: "biweekly",   label: "Every 2 weeks (standard)" },
  { key: "monthly",    label: "Monthly (+20%)" },
  { key: "every_visit", label: "One-time visit" },
];

const TIMES = [
  "Morning (7am-11am)",
  "Afternoon (11am-3pm)",
  "Evening (3pm-7pm)",
];

export function CancelBookingDialog({ booking, open, onOpenChange, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!booking) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/cancel`, { reason });
      toast.success(data.refund_status === "refund_pending"
        ? "Booking canceled — refund is being processed"
        : data.refund_status === "refund_review"
          ? "Booking canceled — refund flagged for review"
          : "Booking canceled");
      onDone && onDone(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cancel-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl">Cancel this booking?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="rounded-xl bg-[#F4F6F5] border border-gray-200 p-3">
            <div className="font-bold">{booking.service_name}</div>
            <div className="text-gray-600">{booking.preferred_date} · {booking.preferred_time}</div>
            <div className="text-gray-600 truncate">{booking.formatted_address || booking.address}</div>
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-bold">Refund policy:</span> &gt;24h notice → full refund. Within 24h → flagged for admin review.
          </div>
          <div>
            <Label htmlFor="cancel-reason" className="text-xs uppercase tracking-[0.16em] text-gray-500 font-bold">Reason (optional)</Label>
            <Textarea id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Anything we should know?" className="mt-1.5" data-testid="cancel-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-full" data-testid="cancel-back-btn">Keep booking</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-confirm-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <XCircle className="w-4 h-4 mr-1.5" /> Cancel booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RescheduleBookingDialog({ booking, open, onOpenChange, onDone }) {
  const [date, setDate] = useState(booking?.preferred_date || "");
  const [time, setTime] = useState(booking?.preferred_time || TIMES[0]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!booking) return null;

  const submit = async () => {
    if (!date) {
      toast.error("Please pick a new date");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/reschedule`, {
        preferred_date: date, preferred_time: time, reason,
      });
      toast.success("Booking rescheduled");
      onDone && onDone(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reschedule.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl">Reschedule</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="rounded-xl bg-[#F4F6F5] border border-gray-200 p-3">
            <div className="font-bold">{booking.service_name}</div>
            <div className="text-gray-600">Current: {booking.preferred_date} · {booking.preferred_time}</div>
          </div>
          <div>
            <Label htmlFor="resch-date" className="text-xs uppercase tracking-[0.16em] text-gray-500 font-bold">New date</Label>
            <Input id="resch-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 mt-1.5" data-testid="reschedule-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.16em] text-gray-500 font-bold">New time</Label>
            <Select value={time} onValueChange={setTime}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="reschedule-time"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="resch-reason" className="text-xs uppercase tracking-[0.16em] text-gray-500 font-bold">Note (optional)</Label>
            <Textarea id="resch-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="reschedule-confirm-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <Calendar className="w-4 h-4 mr-1.5" /> Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SubscriptionDialog({ booking, open, onOpenChange, onDone }) {
  const [newFreq, setNewFreq] = useState(booking?.frequency || "biweekly");
  const [busy, setBusy] = useState(false);
  if (!booking) return null;

  const status = booking.subscription_status || "active";
  const isPaused = status === "paused";
  const isCancelled = status === "cancelled";

  const act = async (action, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.booking_id}/subscription`, { action, ...extra });
      const msg = {
        pause: "Subscription paused",
        resume: "Subscription resumed",
        cancel: "Subscription canceled",
        change_frequency: `Frequency changed to ${data.frequency_label}`,
      }[action] || "Done";
      toast.success(msg);
      onDone && onDone(data);
      if (action === "cancel") onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update subscription.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="subscription-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] text-2xl">Manage subscription</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-gray-700">
          <div className="rounded-xl bg-[#F4F6F5] border border-gray-200 p-3">
            <div className="font-bold">{booking.service_name}</div>
            <div className="text-gray-600">Frequency: {booking.frequency_label}</div>
            <div className="mt-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-[0.16em] border ${
                isPaused ? "bg-amber-50 text-amber-800 border-amber-200" :
                isCancelled ? "bg-gray-100 text-gray-700 border-gray-200" :
                "bg-emerald-50 text-[#047857] border-emerald-200"
              }`}>
                {status}
              </span>
            </div>
          </div>

          {!isCancelled && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-[0.16em] text-gray-500 font-bold">Change frequency</Label>
                <div className="mt-1.5 flex gap-2">
                  <Select value={newFreq} onValueChange={setNewFreq}>
                    <SelectTrigger className="h-11 flex-1" data-testid="sub-freq-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREQS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={() => act("change_frequency", { new_frequency: newFreq })} disabled={busy || newFreq === booking.frequency} className="rounded-full bg-[#062818] hover:bg-black text-white px-4" data-testid="sub-change-freq-btn">
                    <Repeat className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {!isPaused ? (
                  <Button onClick={() => act("pause")} disabled={busy} variant="outline" className="rounded-full" data-testid="sub-pause-btn">
                    <Pause className="w-4 h-4 mr-1.5" /> Pause
                  </Button>
                ) : (
                  <Button onClick={() => act("resume")} disabled={busy} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid="sub-resume-btn">
                    <Play className="w-4 h-4 mr-1.5" /> Resume
                  </Button>
                )}
                <Button onClick={() => act("cancel")} disabled={busy} variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-50" data-testid="sub-cancel-btn">
                  <XCircle className="w-4 h-4 mr-1.5" /> Cancel
                </Button>
              </div>
            </>
          )}

          {isCancelled && (
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 text-sm text-gray-600">
              This subscription was canceled. Past visits remain in your history.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline" className="rounded-full">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
